import { useRef, useCallback, useEffect } from "react";
import { useLoader, useDialog } from "contexts";
import axios from "axios";
import { formatError, isValidValue, logger } from "utils";
import useLocalStorage from "./useLocalStorage";

/**
 * useCancelableDebouncedApi
 * --------------------------
 * Runs API calls with:
 * 1. Cancelling the previous call of this hook (default) — a newer call
 *    replaces an older one that is still waiting or in flight
 * 2. Optional debounce: wait `debounce` ms first, so quick successive calls
 *    end up as one request. Off by default: submits and page loads run
 *    immediately.
 * 3. Optional global loader and error dialog
 *
 * Every call settles: a cancelled call rejects with axios' CanceledError
 * (check with `axios.isCancel(error)`), it never hangs.
 *
 * @param {number} defaultDebounce - debounce (ms) for every call of this hook (default 0)
 *
 * Usage:
 * const { callApi, cancelApi } = useCancelableDebouncedApi();
 * callApi(({ signal }) => api.get(params, { signal }), { debounce: 300 })
 */
const useCancelableDebouncedApi = (defaultDebounce = 0) => {
  const { showLoader, hideLoader } = useLoader();
  const { showError, hideError } = useDialog();
  const [, setAccessToken] = useLocalStorage("access_token", null);

  // The latest call (the one `cancelPrevious` / `cancelApi` cancels)
  const latestRef = useRef(null);
  // Every unfinished call, so all of them are cancelled on unmount
  const activeRef = useRef(new Set());

  /** Cancel one call: stop its timer or abort its request */
  const cancelCall = (call) => {
    if (!call || call.settled) return;
    if (call.timer) {
      clearTimeout(call.timer);
      call.timer = null;
      call.settle(() => call.reject(new axios.CanceledError("Request cancelled")));
    } else {
      call.controller?.abort();
    }
  };

  /** Cancel the latest pending / ongoing API call of this hook */
  const cancelApi = useCallback(() => {
    cancelCall(latestRef.current);
    latestRef.current = null;
  }, []);

  /**
   * callApi
   * --------
   * @param {Function} apiCallFn - ({ signal }) => Promise (the API call)
   * @param {Object} options
   *    showLoader: boolean - show the global loader (default: true)
   *    cancelPrevious: boolean - cancel this hook's previous call (default: true)
   *    debounce: number - wait this many ms before calling (default: hook's value, 0)
   *    errorSubmit: function - callback of the error dialog's button
   *    isErrorShow: boolean - show API errors in the error dialog (default: true)
   * @returns {Promise} API result; rejects on error or cancellation
   */
  const callApi = useCallback(
    (
      apiCallFn,
      {
        showLoader: show = true,
        cancelPrevious = true,
        debounce = defaultDebounce,
        errorSubmit = null,
        isErrorShow = true,
      } = {},
    ) => {
      if (cancelPrevious) cancelApi();

      const promise = new Promise((resolve, reject) => {
        const call = { timer: null, controller: null, settled: false, reject };
        call.settle = (fn) => {
          if (call.settled) return;
          call.settled = true;
          activeRef.current.delete(call);
          if (latestRef.current === call) latestRef.current = null;
          fn();
        };
        activeRef.current.add(call);
        latestRef.current = call;

        const run = async () => {
          call.timer = null;
          const controller = new AbortController();
          call.controller = controller;
          try {
            if (show) showLoader();
            const result = await apiCallFn({ signal: controller.signal });
            // An error answer with status 200 ({ error: true, message })
            if (result?.error && !!result?.message && isErrorShow) {
              showError(result.message, errorSubmit);
            }
            call.settle(() => resolve(result));
          } catch (error) {
            if (!axios.isCancel(error)) {
              const err = formatError(error);
              if (isValidValue(err) && typeof err === "string") {
                if (err.toLowerCase().includes("please log in again to continue")) {
                  // Session can't be refreshed: sign out after the dialog
                  showError(err, () => setAccessToken(null));
                } else if (isErrorShow) {
                  showError(err, errorSubmit);
                }
              }
              logger.error("API call error:", error);
            }
            call.settle(() => reject(error));
          } finally {
            if (show) hideLoader();
          }
        };

        if (debounce > 0) {
          call.timer = setTimeout(run, debounce);
        } else {
          run();
        }
      });
      // Callers that don't await the call (fire and forget) must not cause
      // an "unhandled rejection" when it is cancelled or fails; callers that
      // await it still get the rejection
      promise.catch(() => {});
      return promise;
    },
    [defaultDebounce, cancelApi, showLoader, hideLoader, showError, setAccessToken],
  );

  // Cleanup on unmount: cancel every unfinished call of this component
  useEffect(() => {
    const active = activeRef.current;
    return () => {
      [...active].forEach(cancelCall);
      latestRef.current = null;
      hideError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { callApi, cancelApi };
};

export default useCancelableDebouncedApi;
